import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check } from "lucide-react";

function HeroPanel() {
    return (
        <div className="relative overflow-hidden rounded-3xl border border-nm-line bg-nm-panel p-1 shadow-sm">
            <div
                className="rounded-[20px] p-6 sm:p-8"
                style={{ background: "radial-gradient(120% 130% at 15% 0%, rgba(139,92,246,0.85) 0%, #0b0912 65%)" }}
            >
                <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-white/70">Buenos días, Laura</p>
                    <span className="rounded-full bg-nm-surface/10 px-3 py-1 text-xs font-medium text-white">Ruta 35%</span>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-3">
                    {[["8", "Leads activos"], ["2", "Clientes"], ["5", "Seguimientos"]].map(([n, l]) => (
                        <div key={l} className="rounded-xl bg-nm-surface/10 p-3">
                            <p className="text-2xl font-bold tabular-nums text-white">{n}</p>
                            <p className="mt-1 text-[11px] leading-tight text-white/60">{l}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-4 rounded-2xl bg-nm-surface p-4">
                    <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">Tus acciones de hoy</p>
                    <div className="mt-3 space-y-3">
                        {[
                            ["Marta Giménez", "Hacer seguimiento", "MG"],
                            ["Nuria Belmonte", "Invitar a la presentación", "NB"],
                            ["Carlos Ferrán", "Enviar el primer mensaje", "CF"],
                        ].map(([n, a, i]) => (
                            <div key={n} className="flex items-center gap-3">
                                <span className="grid h-9 w-9 place-items-center rounded-full bg-nm-accent text-xs font-semibold text-white">{i}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-nm-ink">{n}</p>
                                    <p className="truncate text-xs text-nm-muted">{a}</p>
                                </div>
                                <span className="h-5 w-5 shrink-0 rounded-md border border-nm-line" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const BENEFITS = [
    {
        title: "Sabes a quién escribir hoy",
        body: "Network Mastery organiza tus contactos, distingue clientes de posibles socios y te dice a quién contactar y cuándo hacer seguimiento.",
    },
    {
        title: "Una ruta paso a paso",
        body: "Desde tu primera lista hasta tu primer cliente y tu primer miembro de equipo. Sin depender de tu sponsor para saber qué toca.",
    },
    {
        title: "Se duplica solo",
        body: "Cuando alguien se une a tu equipo con tu enlace, arranca su propia ruta. Tú acompañas; el sistema enseña.",
    },
];

export default function Landing() {
    const { user } = useAuth();
    const appLink = user ? "/app" : "/registro";

    return (
        <div className="min-h-screen bg-nm-bg">
            {/* Navegación */}
            <header className="sticky top-0 z-30 border-b border-nm-line bg-nm-bg/90 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
                    <Wordmark />
                    <div className="flex items-center gap-2">
                        <Link to="/entrar" data-testid="nav-login-button">
                            <Button variant="ghost" className="text-nm-ink hover:bg-nm-accent-soft">Entrar</Button>
                        </Link>
                        <Link to={appLink} data-testid="nav-register-button">
                            <Button className="rounded-full">Empezar gratis</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Portada */}
            <section className="relative overflow-hidden">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] grain opacity-70"
                    style={{ background: "radial-gradient(80% 60% at 70% 0%, rgba(139,92,246,0.10) 0%, rgba(250,249,251,0) 70%)" }} />
                <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24">
                    <div className="animate-fade-up">
                        <span className="inline-flex items-center gap-2 rounded-full border border-nm-line bg-nm-surface px-3 py-1 text-xs font-medium text-nm-muted">
                            Gratis mientras crece tu red
                        </span>
                        <h1 className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight text-nm-ink text-balance sm:text-5xl lg:text-6xl">
                            Capta clientes y construye equipo, <span className="gradient-text">sin depender de tu sponsor.</span>
                        </h1>
                        <p className="mt-5 max-w-prose text-lg leading-relaxed text-nm-muted">
                            Network Mastery te guía paso a paso: qué hacer hoy, a quién escribir y cómo
                            convertir cada contacto en cliente o en un nuevo miembro de tu equipo.
                        </p>
                        <div className="mt-8">
                            <Link to={appLink} data-testid="hero-cta-button">
                                <Button size="lg" className="h-12 rounded-full px-7 text-base">
                                    Empezar gratis <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                        <p className="mt-3 text-sm text-nm-muted">Sin tarjeta. Empiezas aunque partas de cero.</p>
                    </div>
                    <div className="animate-fade-up [animation-delay:120ms]">
                        <HeroPanel />
                    </div>
                </div>
            </section>

            {/* Qué hace */}
            <section className="border-t border-nm-line bg-nm-surface">
                <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
                    <h2 className="max-w-2xl text-3xl font-bold tracking-tight text-nm-ink sm:text-4xl">
                        Todo lo que necesitas para empezar y no perderte.
                    </h2>
                    <div className="mt-14 grid gap-8 md:grid-cols-3">
                        {BENEFITS.map((b, i) => (
                            <div key={b.title} className="rounded-2xl border border-nm-line bg-nm-bg p-7">
                                <span className="grid h-11 w-11 place-items-center rounded-xl bg-nm-accent text-lg font-bold text-white tabular-nums">
                                    {i + 1}
                                </span>
                                <h3 className="mt-5 text-lg font-semibold text-nm-ink">{b.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-nm-muted">{b.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Para quién es */}
            <section className="border-t border-nm-line">
                <div className="mx-auto grid max-w-6xl gap-10 px-6 py-20 lg:grid-cols-2 lg:py-28">
                    <div>
                        <h2 className="text-3xl font-bold tracking-tight text-nm-ink sm:text-4xl">¿Es para ti?</h2>
                        <p className="mt-4 max-w-prose text-nm-muted">
                            Si quieres empezar o crecer en network marketing y no sabes por dónde,
                            Network Mastery es tu punto de partida.
                        </p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {[
                            ["Empiezas desde cero", "No tienes contactos ni base de datos. Te ayudamos a construirla desde hoy."],
                            ["Ya eres networker", "Tienes equipo pero te falta un sistema constante y duplicable."],
                            ["Quieres captar clientes", "Aprende a atraer y cerrar sin sonar a vendedor."],
                            ["Quieres construir equipo", "Incorpora personas y ayúdalas a repetir tu sistema."],
                        ].map(([t, d]) => (
                            <div key={t} className="rounded-2xl border border-nm-line bg-nm-surface p-5">
                                <div className="flex items-center gap-2">
                                    <span className="grid h-6 w-6 place-items-center rounded-full bg-nm-accent-soft">
                                        <Check className="h-3.5 w-3.5 text-nm-accent" />
                                    </span>
                                    <h3 className="font-semibold text-nm-ink">{t}</h3>
                                </div>
                                <p className="mt-2 text-sm leading-relaxed text-nm-muted">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Llamada final */}
            <section className="border-t border-nm-line">
                <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
                    <div className="relative overflow-hidden rounded-3xl bg-nm-panel px-8 py-16 text-center sm:px-16">
                        <div className="absolute inset-0 grain opacity-60"
                            style={{ background: "radial-gradient(90% 120% at 50% 0%, rgba(139,92,246,0.7) 0%, #0b0912 60%)" }} />
                        <div className="relative">
                            <h2 className="mx-auto max-w-2xl text-3xl font-bold leading-tight tracking-tight text-white text-balance sm:text-4xl">
                                Tu próxima captación empieza hoy.
                            </h2>
                            <p className="mx-auto mt-4 max-w-md text-white/70">
                                Crea tu cuenta gratis y recibe tu ruta personalizada en un minuto.
                            </p>
                            <Link to={appLink} data-testid="footer-cta-button" className="mt-8 inline-block">
                                <Button size="lg" className="h-12 rounded-full btn-grad px-7 text-base text-white hover:opacity-90">
                                    Empezar gratis <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
